/**
 * Edit Block Processor
 * Feature: 069-markdown-edits-migration
 *
 * Utility for parsing ```edit code blocks from markdown,
 * validating crop/light JSON data, and removing blocks from text.
 */

import type { CropData, LightEditData, CombinedEditData, ParsedEdit, EditBlockParseResult } from '@/types/edit.types';
import logger from '@/utils/logger';

/**
 * Validation constraints for edit blocks
 */
export const EDIT_CONSTRAINTS = {
  // Crop parameters
  COORDINATE_RANGE: { min: 0, max: 100 },
  MIN_SIZE: 5, // Minimum crop dimension %
  VALID_RATIOS: ['free', '1:1', '4:3', '3:2', '16:9', '2:3', '3:4', '9:16', '24:65', '65:24'] as const,

  // Light parameters
  EXPOSURE_RANGE: { min: -2, max: 2 },
  CONTRAST_RANGE: { min: -1, max: 1 },
  TONAL_RANGE: { min: -100, max: 100 }, // highlights, shadows, whites, blacks
} as const;

/**
 * Clamp a value to a range, handling floating-point rounding errors
 */
function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * Custom error for edit validation failures
 */
export class EditValidationError extends Error {
  constructor(public field: string, message: string, public value?: unknown) {
    super(`Invalid ${field}: ${message}`);
    this.name = 'EditValidationError';
  }
}

/**
 * Internal structure for detected edit blocks
 */
interface EditBlock {
  content: string;
  startLine: number;
  endLine: number;
}

/**
 * Parse markdown for ```edit code blocks and extract crop/light data
 *
 * @param markdown - Raw markdown text from comment input
 * @returns Parsed edits, cleaned markdown, and any validation errors
 */
export function parseEditBlocks(markdown: string): EditBlockParseResult {
  logger.debug('EditBlockProcessor', 'Parsing markdown for edit blocks', { markdownLength: markdown.length });

  const blocks: EditBlock[] = [];
  const allEdits: ParsedEdit[] = [];
  const errors: string[] = [];

  // Detect ```edit blocks using line-by-line parsing
  const lines = markdown.split('\n');
  let inBlock = false;
  let blockStartLine = -1;
  let blockContent: string[] = [];

  lines.forEach((line, index) => {
    const trimmed = line.trim();

    if (!inBlock && trimmed === '```edit') {
      // Start of edit block
      inBlock = true;
      blockStartLine = index;
      blockContent = [];
    } else if (inBlock && trimmed === '```') {
      // End of edit block
      inBlock = false;
      blocks.push({
        content: blockContent.join('\n'),
        startLine: blockStartLine,
        endLine: index,
      });
      blockContent = [];
    } else if (inBlock) {
      // Inside block - collect content
      blockContent.push(line);
    }
  });

  // Check for unclosed block
  if (inBlock) {
    errors.push(`Unclosed edit block starting at line ${blockStartLine + 1}`);
  }

  logger.info('EditBlockProcessor', `Found ${blocks.length} edit block(s)`);

  // Parse and validate each block
  blocks.forEach((block, blockIndex) => {
    try {
      const parsed = JSON.parse(block.content);

      if (!parsed || typeof parsed !== 'object') {
        errors.push(`Block ${blockIndex + 1}: Expected JSON object`);
        return;
      }

      // Validate at least one of crop or light exists
      if (!parsed.crop && !parsed.light) {
        errors.push(`Block ${blockIndex + 1}: Must have at least one of "crop" or "light"`);
        return;
      }

      const validatedData: CombinedEditData = {};
      const blockErrors: string[] = [];

      // Validate crop if present
      if (parsed.crop) {
        try {
          validatedData.crop = validateCropData(parsed.crop);
        } catch (error) {
          if (error instanceof EditValidationError) {
            blockErrors.push(error.message);
          } else {
            blockErrors.push(`Crop validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
          }
        }
      }

      // Validate light if present
      if (parsed.light) {
        try {
          validatedData.light = validateLightData(parsed.light);
        } catch (error) {
          if (error instanceof EditValidationError) {
            blockErrors.push(error.message);
          } else {
            blockErrors.push(`Light validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
          }
        }
      }

      // Add errors for this block
      errors.push(...blockErrors.map(err => `Block ${blockIndex + 1}: ${err}`));

      // Only add edit if at least one part validated successfully
      if (validatedData.crop || validatedData.light) {
        allEdits.push({
          type: 'edit',
          data: validatedData,
        });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      errors.push(`Block ${blockIndex + 1}: Invalid JSON - ${message}`);
      logger.warn('EditBlockProcessor', `Failed to parse block ${blockIndex + 1}`, { error });
    }
  });

  // Remove blocks from markdown
  const cleanedMarkdown = removeEditBlocks(
    markdown,
    blocks.map(b => ({ start: b.startLine, end: b.endLine }))
  );

  logger.info('EditBlockProcessor', `Parsed ${allEdits.length} valid edit(s)`, { errorCount: errors.length });

  return {
    edits: allEdits,
    cleanedMarkdown,
    errors,
  };
}

/**
 * Validate crop data against constraints, clamping values to bounds
 * to handle floating-point rounding errors at image edges
 */
export function validateCropData(data: unknown): CropData {
  if (!data || typeof data !== 'object') {
    throw new EditValidationError('crop', 'Must be an object', data);
  }

  const crop = data as Record<string, unknown>;

  // Validate required numeric fields exist (allow slightly out-of-range for floating-point rounding)
  // Clamping below will correct small rounding errors at boundaries
  const TOLERANCE = 1; // Allow values up to 101 to handle floating-point precision errors
  validateNumericField(crop, 'x', EDIT_CONSTRAINTS.COORDINATE_RANGE.min - TOLERANCE, EDIT_CONSTRAINTS.COORDINATE_RANGE.max + TOLERANCE);
  validateNumericField(crop, 'y', EDIT_CONSTRAINTS.COORDINATE_RANGE.min - TOLERANCE, EDIT_CONSTRAINTS.COORDINATE_RANGE.max + TOLERANCE);
  validateNumericField(crop, 'width', 0.1, EDIT_CONSTRAINTS.COORDINATE_RANGE.max + TOLERANCE);
  validateNumericField(crop, 'height', 0.1, EDIT_CONSTRAINTS.COORDINATE_RANGE.max + TOLERANCE);

  // Clamp values to valid ranges (handles floating-point rounding at edges)
  // Only clamp DOWN to fit within bounds, not UP (which would change user intent)
  let x = clamp(crop.x as number, 0, 100);
  let y = clamp(crop.y as number, 0, 100);
  let width = crop.width as number;
  let height = crop.height as number;

  // Cap at 100 if exceeding (handles floating-point precision errors)
  if (width > 100) width = 100;
  if (height > 100) height = 100;

  // Clamp to fit within image bounds
  if (x + width > 100) {
    width = 100 - x;
  }
  if (y + height > 100) {
    height = 100 - y;
  }

  // Validate minimum dimensions (user intent, not a rounding error)
  if (width < EDIT_CONSTRAINTS.MIN_SIZE) {
    throw new EditValidationError('crop.width', `Must be at least ${EDIT_CONSTRAINTS.MIN_SIZE}%`, width);
  }
  if (height < EDIT_CONSTRAINTS.MIN_SIZE) {
    throw new EditValidationError('crop.height', `Must be at least ${EDIT_CONSTRAINTS.MIN_SIZE}%`, height);
  }

  // Validate aspect ratio if present
  let aspectRatio: CropData['aspectRatio'] = 'free';
  if (crop.aspectRatio !== undefined) {
    if (typeof crop.aspectRatio !== 'string') {
      throw new EditValidationError('crop.aspectRatio', 'Must be a string', crop.aspectRatio);
    }
    if (!EDIT_CONSTRAINTS.VALID_RATIOS.includes(crop.aspectRatio as typeof EDIT_CONSTRAINTS.VALID_RATIOS[number])) {
      throw new EditValidationError(
        'crop.aspectRatio',
        `Must be one of: ${EDIT_CONSTRAINTS.VALID_RATIOS.join(', ')}`,
        crop.aspectRatio
      );
    }
    aspectRatio = crop.aspectRatio as CropData['aspectRatio'];
  }

  const result: CropData = {
    x,
    y,
    width,
    height,
    aspectRatio,
  };

  // Include computedAspectRatio if present
  if (typeof crop.computedAspectRatio === 'number') {
    result.computedAspectRatio = crop.computedAspectRatio;
  }

  return result;
}

/**
 * Validate light adjustment data against constraints
 */
export function validateLightData(data: unknown): LightEditData {
  if (!data || typeof data !== 'object') {
    throw new EditValidationError('light', 'Must be an object', data);
  }

  const light = data as Record<string, unknown>;

  // All light fields default to 0 if not provided
  const exposure = validateOptionalNumericField(
    light,
    'exposure',
    EDIT_CONSTRAINTS.EXPOSURE_RANGE.min,
    EDIT_CONSTRAINTS.EXPOSURE_RANGE.max
  ) ?? 0;

  const contrast = validateOptionalNumericField(
    light,
    'contrast',
    EDIT_CONSTRAINTS.CONTRAST_RANGE.min,
    EDIT_CONSTRAINTS.CONTRAST_RANGE.max
  ) ?? 0;

  const highlights = validateOptionalNumericField(
    light,
    'highlights',
    EDIT_CONSTRAINTS.TONAL_RANGE.min,
    EDIT_CONSTRAINTS.TONAL_RANGE.max
  ) ?? 0;

  const shadows = validateOptionalNumericField(
    light,
    'shadows',
    EDIT_CONSTRAINTS.TONAL_RANGE.min,
    EDIT_CONSTRAINTS.TONAL_RANGE.max
  ) ?? 0;

  const whites = validateOptionalNumericField(
    light,
    'whites',
    EDIT_CONSTRAINTS.TONAL_RANGE.min,
    EDIT_CONSTRAINTS.TONAL_RANGE.max
  ) ?? 0;

  const blacks = validateOptionalNumericField(
    light,
    'blacks',
    EDIT_CONSTRAINTS.TONAL_RANGE.min,
    EDIT_CONSTRAINTS.TONAL_RANGE.max
  ) ?? 0;

  return {
    exposure,
    contrast,
    highlights,
    shadows,
    whites,
    blacks,
  };
}

/**
 * Validate a required numeric field
 */
function validateNumericField(
  obj: Record<string, unknown>,
  field: string,
  min: number,
  max: number
): void {
  const value = obj[field];

  if (typeof value !== 'number') {
    throw new EditValidationError(`crop.${field}`, 'Must be a number', value);
  }

  if (value < min || value > max) {
    throw new EditValidationError(
      `crop.${field}`,
      `Must be between ${min} and ${max}`,
      value
    );
  }
}

/**
 * Validate an optional numeric field
 */
function validateOptionalNumericField(
  obj: Record<string, unknown>,
  field: string,
  min: number,
  max: number
): number | undefined {
  const value = obj[field];

  if (value === undefined) {
    return undefined;
  }

  if (typeof value !== 'number') {
    throw new EditValidationError(`light.${field}`, 'Must be a number', value);
  }

  if (value < min || value > max) {
    throw new EditValidationError(
      `light.${field}`,
      `Must be between ${min} and ${max}`,
      value
    );
  }

  return value;
}

/**
 * Internal: Remove edit blocks from markdown using line ranges
 */
function removeEditBlocks(
  markdown: string,
  blockRanges: Array<{ start: number; end: number }>
): string {
  if (blockRanges.length === 0) {
    return markdown;
  }

  const lines = markdown.split('\n');
  const filteredLines = lines.filter((_line, index) => {
    return !blockRanges.some(range => index >= range.start && index <= range.end);
  });

  // Join and then clean up multiple consecutive blank lines
  let result = filteredLines.join('\n');

  // Replace multiple consecutive newlines with just two newlines (preserves paragraph breaks)
  result = result.replace(/\n{3,}/g, '\n\n');

  return result.trim();
}
